import type { OnboardingPipelineSummaryDto } from './onboardingPipelineStatus';

export type VmsServiceJourneyStage =
  | 'pre_onboarding'
  | 'onboarding'
  | 'contract_review'
  | 'vendor_live';

export type VmsServiceJourneyStepUi = 'done' | 'current' | 'pending';

export const VMS_SERVICE_JOURNEY_STAGES: readonly {
  key: VmsServiceJourneyStage;
  label: string;
}[] = [
  { key: 'pre_onboarding', label: 'PreOnboarding' },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'contract_review', label: 'Contract Review' },
  { key: 'vendor_live', label: 'Vendor Live' },
] as const;

const STAGE_ORDER: VmsServiceJourneyStage[] = [
  'pre_onboarding',
  'onboarding',
  'contract_review',
  'vendor_live',
];

/** Derive macro journey position from onboarding pipeline summary (registered vendor). */
export function resolveServiceJourneyStageFromPipeline(
  summary: OnboardingPipelineSummaryDto | null | undefined,
): VmsServiceJourneyStage {
  if (!summary) return 'onboarding';
  /** Register filling approved and vendor persisted — live on main Register. */
  if (summary.all_complete) return 'vendor_live';
  /** Prerequisites done — register filling in progress; macro contract step is complete. */
  if (summary.prerequisites_complete) return 'vendor_live';
  if (
    summary.annexure === 'done' &&
    summary.dpia === 'done' &&
    summary.contract === 'done' &&
    summary.notification_regulator === 'done'
  ) {
    return 'vendor_live';
  }
  if (
    summary.annexure === 'done' &&
    summary.dpia === 'done' &&
    summary.notification_regulator === 'done'
  ) {
    return 'contract_review';
  }
  return 'onboarding';
}

/** Route-level fallback when pipeline data is unavailable. */
export function resolveServiceJourneyStageFromRoute(pathname: string): VmsServiceJourneyStage | null {
  if (pathname === '/vms/pre-onboarding' || pathname.startsWith('/vms/pre-onboarding/')) {
    return 'pre_onboarding';
  }
  if (
    pathname === '/vms/onboarding' ||
    pathname.startsWith('/vms/onboarding/') ||
    pathname === '/vms/onboarding2' ||
    pathname.startsWith('/vms/onboarding2/')
  ) {
    return 'onboarding';
  }
  if (pathname.startsWith('/vms/ongoing-management')) {
    return 'vendor_live';
  }
  return null;
}

/** Macro journey strip (PreOnboarding → Vendor Live) only on pre-onboarding and onboarding pages. */
export function shouldShowVmsServiceJourneyStrip(pathname: string): boolean {
  if (pathname === '/vms/pre-onboarding' || pathname.startsWith('/vms/pre-onboarding/')) {
    return true;
  }
  if (
    pathname === '/vms/onboarding' ||
    pathname.startsWith('/vms/onboarding/') ||
    pathname === '/vms/onboarding2' ||
    pathname.startsWith('/vms/onboarding2/')
  ) {
    return true;
  }
  return false;
}

/**
 * Onboarding drill-down pages render `OnboardingVendorContextStrip` — hide global
 * `VmsThreadHeader` so active-service + journey strip is not duplicated.
 */
export function shouldHideVmsThreadHeader(pathname: string): boolean {
  if (/^\/vms\/onboarding\/vendors\/\d+/.test(pathname)) return true;
  if (pathname === '/vms/onboarding/annexures') return true;
  return false;
}

export function resolveServiceJourneyStage(input: {
  pipeline?: OnboardingPipelineSummaryDto | null;
  routePathname?: string;
  initiationApproved?: boolean;
}): VmsServiceJourneyStage {
  if (input.pipeline) return resolveServiceJourneyStageFromPipeline(input.pipeline);
  const fromRoute = input.routePathname
    ? resolveServiceJourneyStageFromRoute(input.routePathname)
    : null;
  if (fromRoute) return fromRoute;
  if (input.initiationApproved) return 'onboarding';
  return 'pre_onboarding';
}

export function buildServiceJourneyStepUi(
  currentStage: VmsServiceJourneyStage,
): Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi> {
  /** Vendor Live is terminal — vendor is on Register; show the full journey completed. */
  if (currentStage === 'vendor_live') {
    return STAGE_ORDER.reduce(
      (acc, key) => {
        acc[key] = 'done';
        return acc;
      },
      {} as Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi>,
    );
  }

  const currentIndex = STAGE_ORDER.indexOf(currentStage);
  return STAGE_ORDER.reduce(
    (acc, key, index) => {
      acc[key] = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'pending';
      return acc;
    },
    {} as Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi>,
  );
}

/**
 * Macro journey pills from pipeline fields — e.g. contract review can be done while annexure is still open.
 */
export function buildServiceJourneyStepUiFromPipeline(
  summary: OnboardingPipelineSummaryDto | null | undefined,
  options: {
    initiationApproved?: boolean;
    fallbackCurrentStage?: VmsServiceJourneyStage;
  } = {},
): Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi> {
  if (!summary) {
    return buildServiceJourneyStepUi(
      options.fallbackCurrentStage ??
        (options.initiationApproved ? 'onboarding' : 'pre_onboarding'),
    );
  }

  if (summary.all_complete) {
    return buildServiceJourneyStepUi('vendor_live');
  }

  if (summary.prerequisites_complete) {
    return {
      pre_onboarding: 'done',
      onboarding: 'done',
      contract_review: 'done',
      vendor_live: 'current',
    };
  }

  const ui: Record<VmsServiceJourneyStage, VmsServiceJourneyStepUi> = {
    pre_onboarding: options.initiationApproved !== false ? 'done' : 'pending',
    onboarding: 'current',
    contract_review: 'pending',
    vendor_live: 'pending',
  };

  if (summary.contract === 'done') {
    ui.contract_review = 'done';
  }

  const preContractDone =
    summary.annexure === 'done' &&
    summary.dpia === 'done' &&
    summary.notification_regulator === 'done';

  if (preContractDone) {
    ui.onboarding = 'done';
    ui.contract_review = summary.contract === 'done' ? 'done' : 'current';
  }

  return ui;
}
